import "pixi";
import "phaser";

import * as cursor from "../cursor";
import * as i18n from "../i18n";
import * as logging from "../logging";
import * as progression from "../progression";
import AudioEngine from "../audio_engine";
import BackButton from "../ui/back";
import Fog from "../fog";


export default class CongratulationsState extends Phaser.State {
    bg: Phaser.Sprite;
    title: i18n.Text;
    titleMask: Phaser.Graphics;
    playTriangle: Phaser.Sprite;
    thanks: i18n.Text;
    us: i18n.Text;
    back: BackButton;
    fogBackground: Fog;

    create() {
        AudioEngine.get().switchTo("regular");

        this.game.stage.backgroundColor = "#FFFFFF";
        logging.startLevel(logging.LEVEL_CONGRATULATIONS, null);
        this.bg = this.game.add.sprite(0, 0, !progression.challengesFinished() ? "background7" : "background8");
        this.bg.width = this.game.width;
        this.bg.height = this.game.height;

        this.fogBackground = new Fog(this.game);

        this.back = new BackButton(this.game, () => {
            logging.finishLevel();
            this.state.start("title", true, false, true);
        });
        this.back.enable();

        this.titleMask = this.game.add.graphics(0, 0);
        let mask = { width: 0 }
        const updateMask = () => {
            this.titleMask.clear();
            this.titleMask.beginFill(0xFFFFFF);
            this.titleMask.drawRect(0, 0, mask.width, this.game.height);
        };

        this.title = i18n.label(this.game, this.game.width / 2, 128, i18n._("CONGRATULATIONS"), "font-large", 88);
        this.title.anchor.set(0.5, 0);
        this.title.mask = this.titleMask;

        this.thanks = i18n.label(
            this.game, this.game.width / 2,
            128 + i18n.labelHeight(this.title) + 64,
            !progression.challengesFinished() ?
                i18n._("Play challenge levels") :
                i18n._("Thank you for playing!"),
            "font-large", 48);

        if (!progression.challengesFinished()) {
            this.thanks.inputEnabled = true;
            cursor.instance().addClickable(this.thanks.input);
            const origX = this.thanks.x - (this.thanks.width / 2) - 50;
            this.playTriangle = this.game.add.sprite(
                origX,
                this.thanks.y + (i18n.labelHeight(this.thanks) / 2), "triangle");
            this.playTriangle.anchor.setTo(0.5, 0.5);
            this.playTriangle.scale.setTo(1.5, 1.5);

            let tween : Phaser.Tween | null = null;
            this.thanks.events.onInputOver.add(() => {
                AudioEngine.get().play("button_hover");
                if (tween) tween.stop();
                tween = this.game.add.tween(this.playTriangle).to(
                    { x: origX + 30 }, 60, Phaser.Easing.Quadratic.Out, true);
            });
            this.thanks.events.onInputOut.add(() => {
                if (tween) tween.stop();
                tween = this.game.add.tween(this.playTriangle).to(
                    { x: origX }, 60, Phaser.Easing.Quadratic.Out, true);
            });

            this.thanks.events.onInputDown.add(() => {
                AudioEngine.get().play("button_click");
                this.state.start("level-select");
            });
        }

        this.thanks.anchor.set(0.5, 0);

        this.us = i18n.label(
            this.game, this.game.width / 2, 128 + i18n.labelHeight(this.title) + 64 + i18n.labelHeight(this.thanks) + 48,
            i18n._("Developed By:\nCrystal Liu, Yuhan Wu, \nMichael Mauer, David Li, \nJoel Hoover, and Tomasz Chmielewski"),
            "font-small", 32);
        this.us.anchor.set(0.5, 0);
        this.us.align = "center";

        const group = this.game.add.group();
        group.add(this.thanks);
        if (this.playTriangle) group.add(this.playTriangle);
        group.add(this.us);
        group.alpha = 0;
        group.position.y = this.game.height;

        const emitter = this.game.add.emitter(this.game.width / 2, this.game.height, 75);
        emitter.makeParticles("victory-star");
        emitter.minParticleSpeed.setTo(-300, -1500);
        emitter.maxParticleSpeed.setTo(300, -1000);
        emitter.minParticleScale = 0.1;
        emitter.maxParticleScale = 0.8;
        emitter.gravity.setTo(0, 1000);

        updateMask();
        this.game.add.tween(mask).to({
            width: this.game.width,
        }, 1000, Phaser.Easing.Cubic.In)
            .onUpdateCallback(updateMask)
            .start()
            .chain(
                this.game.add.tween(group).to({
                    alpha: 1,
                    y: 0,
                }, 500, Phaser.Easing.Cubic.InOut).delay(750)
            )
            .onComplete.add(() => {
                emitter.start(true, 10000, 0, 75);
                updateMask();
                this.bg.alpha = 0.5;
                this.game.add.tween(this.bg).to({
                    alpha: 1
                }, 500, Phaser.Easing.Cubic.In).start();

                this.game.time.events.add(2000, this.firework.bind(this));
            });
    }

    update() {
        cursor.instance().update();
    }

    firework() {
        const centerX = this.game.width * Math.random();
        const centerY = this.game.height / 3 * Math.random();

        const spawnX = this.game.width * Math.random();
        const projectile = this.game.add.sprite(spawnX, this.game.height, "victory-star");
        projectile.anchor.setTo(0.5, 0.5);
        projectile.scale.setTo(0.3, 0.3);

        const time = { t: 0 };
        const dx = centerX - spawnX;
        const g = 400;
        const v0 = centerY - this.game.height - 0.5 * g;
        const tween = this.game.add.tween(time).to({ t: 1 }).onUpdateCallback(() => {
            projectile.x = spawnX + time.t * dx;
            projectile.y = 0.5 * g * time.t * time.t + v0 * time.t + this.game.height;
        });
        tween.onComplete.add(() => {
            this.explosion(centerX, centerY);
            projectile.destroy(true);
        });
        tween.start();
    }

    explosion(centerX: number, centerY: number) {
        const numParticles = 16 + Math.floor(16 * Math.random());

        let delay = 0;

        for (let j = 0; j < 1 + Math.floor(3 * Math.random()); j++) {
            const scale = 0.2 + 0.3 * Math.random();
            const duration = 500 + Math.floor(1000 * Math.random());
            this.game.time.events.add(delay, () => { AudioEngine.get().play("firework"); });
            for (let i = 0; i < numParticles; i++) {
                const particle = this.game.add.sprite(centerX, centerY, "victory-star");
                particle.anchor.setTo(0.5, 0.5);
                particle.scale.setTo(scale, scale);
                particle.visible = false;
                const angle = i * 2 * Math.PI / numParticles;
                const tw = this.game.add.tween(particle).to({
                    x: centerX + 200 * Math.cos(angle),
                    y: centerY + 200 * Math.sin(angle),
                    alpha: 0,
                }, duration, Phaser.Easing.Quadratic.Out).delay(delay).start();
                tw.onComplete.add(() => { particle.destroy(true); });
                tw.onStart.add(() => { particle.visible = true; });
            }
            delay += 300 + Math.floor(300 * Math.random());
        }
        this.game.time.events.add(1500 + Math.floor(1000 * Math.random()), this.firework.bind(this));
    }
}



// WEBPACK FOOTER //
// ./src/states/congrats.ts